import {
  applyDeviceCompatibility,
  listDeviceCompatibility,
  listDeviceCompatibilityBatches,
  undoDeviceCompatibility,
  type DeviceCompatibilityRow,
} from '@/lib/api/device-suggest.js';
import { listDeviceMakes } from '@/lib/api/devices.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

export function DeviceCompatibilityCleanupPage(): ReactElement {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'missing' | 'additional'>('missing');
  const [confidence, setConfidence] = useState<'high' | 'medium' | 'low' | 'any'>('high');
  const [suggestion, setSuggestion] = useState<'has' | 'none' | 'any'>('any');
  const [makeId, setMakeId] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: () => listDeviceMakes() });
  const listQuery = useQuery({
    queryKey: ['device-compat-cleanup', mode, confidence, suggestion, makeId, category, page],
    queryFn: () =>
      listDeviceCompatibility({
        mode,
        confidence,
        suggestion,
        page,
        ...(makeId ? { makeId } : {}),
        ...(category ? { category } : {}),
      }),
  });
  const batchesQuery = useQuery({
    queryKey: ['device-compat-batches'],
    queryFn: listDeviceCompatibilityBatches,
  });

  const rows = listQuery.data?.data ?? [];
  const total = (listQuery.data?.meta as { total?: number } | undefined)?.total ?? 0;

  useEffect(() => {
    const next = new Set<string>();
    for (const row of rows) {
      if (row.suggestions.some((item) => item.confidence === 'high')) next.add(row.id);
    }
    setSelected(next);
  }, [listQuery.data]);

  function modelIdsFor(row: DeviceCompatibilityRow): string[] {
    const pool =
      confidence === 'any'
        ? row.suggestions
        : row.suggestions.filter((item) => item.confidence === confidence);
    const highs = pool.filter((item) => item.confidence === 'high');
    const chosen = highs.length > 0 && confidence === 'high' ? highs : pool;
    return chosen.map((item) => item.modelId);
  }

  const applyMut = useMutation({
    mutationFn: () => {
      const items = rows
        .filter((row) => selected.has(row.id))
        .map((row) => ({ itemId: row.id, modelIds: modelIdsFor(row) }))
        .filter((item) => item.modelIds.length > 0);
      if (items.length === 0) throw new Error('Select items that have a suggestion');
      return applyDeviceCompatibility({ mode, items });
    },
    onSuccess: (result) => {
      toastSuccess(`Updated ${result.updated} item${result.updated === 1 ? '' : 's'}`);
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['device-compat-cleanup'] });
      void queryClient.invalidateQueries({ queryKey: ['device-compat-batches'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const undoMut = useMutation({
    mutationFn: (id: string) => undoDeviceCompatibility(id),
    onSuccess: () => {
      toastSuccess('Compatibility batch undone');
      void queryClient.invalidateQueries({ queryKey: ['device-compat-cleanup'] });
      void queryClient.invalidateQueries({ queryKey: ['device-compat-batches'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-950">Device compatibility</h1>
        <p className="mt-1 text-sm text-slate-500">
          Suggest devices from part names. Nothing is linked until you apply, and each batch can be
          undone.{' '}
          <Link to="/settings/data-cleanup/balances" className="font-semibold text-primary hover:underline">
            Balance cleanup
          </Link>
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={mode}
          onChange={(event) => {
            setMode(event.target.value as typeof mode);
            setPage(1);
            setSelected(new Set());
          }}
        >
          <option value="missing">No compatible devices</option>
          <option value="additional">Suggest additional</option>
        </select>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={confidence}
          onChange={(event) => {
            setConfidence(event.target.value as typeof confidence);
            setPage(1);
          }}
        >
          <option value="high">High confidence</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="any">Any confidence</option>
        </select>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={suggestion}
          onChange={(event) => {
            setSuggestion(event.target.value as typeof suggestion);
            setPage(1);
          }}
        >
          <option value="any">Has or no suggestion</option>
          <option value="has">Has a suggestion</option>
          <option value="none">No suggestion</option>
        </select>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={makeId}
          onChange={(event) => {
            setMakeId(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All makes</option>
          {(makesQuery.data ?? []).map((make) => (
            <option key={make.id} value={make.id}>
              {make.name}
            </option>
          ))}
        </select>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All categories</option>
          <option value="repair_part">Repair part</option>
          <option value="retail_accessory">Retail accessory</option>
          <option value="trade_in">Trade-in</option>
          <option value="refurbished">Refurbished</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {rows.map((row) => {
          const isChecked = selected.has(row.id);
          return (
            <label key={row.id} className="flex gap-3 border-b border-slate-100 px-4 py-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={isChecked}
                disabled={row.suggestions.length === 0}
                onChange={(event) => {
                  const next = new Set(selected);
                  if (event.target.checked) next.add(row.id);
                  else next.delete(row.id);
                  setSelected(next);
                }}
              />
              <span>
                <span className="font-medium text-slate-950">{row.name}</span>
                {row.autoSuggested ? (
                  <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    auto-suggested
                  </span>
                ) : null}
                <span className="mt-1 block text-slate-600">
                  {row.suggestions.length === 0
                    ? 'No match'
                    : row.suggestions
                        .map((item) => `${item.makeName} ${item.modelName} (${item.confidence})`)
                        .join(', ')}
                </span>
              </span>
            </label>
          );
        })}
        {rows.length === 0 && !listQuery.isLoading ? (
          <p className="px-4 py-6 text-sm text-slate-500">No items match these filters.</p>
        ) : null}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          disabled={applyMut.isPending}
          onClick={() => applyMut.mutate()}
        >
          {applyMut.isPending ? 'Applying…' : 'Apply selected'}
        </button>
        <div className="flex items-center gap-2 text-sm">
          <button type="button" className="rounded-lg border px-3 py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>
            Page {page} · {total} items
          </span>
          <button
            type="button"
            className="rounded-lg border px-3 py-1"
            disabled={page * 40 >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-700">Recent batches</h2>
        <ul className="mt-2 space-y-2">
          {(batchesQuery.data ?? []).map((batch) => (
            <li key={batch.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <span>
                {batch.ticketCount} items · {new Date(batch.performedAt).toLocaleString()}
                {batch.reversedAt ? ' · undone' : ''}
              </span>
              {batch.reversedAt ? null : (
                <button
                  type="button"
                  className="text-sm font-semibold text-primary"
                  onClick={() => undoMut.mutate(batch.id)}
                >
                  Undo
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
